import type { ResponsiveModalProps } from '@repo/shared/components';
import { BottomsheetExtended, ResponsiveModal } from '@repo/shared/components';
import { useParams } from '@tanstack/react-router';
import { Fragment, useCallback, useEffect, useState } from 'react';
import type { BookingSuccessResponse } from '@/api';
import type {
	RescheduleCabOption,
	RescheduleFareData,
} from '../../-api/reschedule';
import { RescheduleCabSelection } from './components/cab-selection';
import { PaymentBreakdown } from './components/payment-breakdown';
import { RescheduleContent } from './components/reschedule-content';
import { useRescheduleActions } from './hooks/use-reschedule-actions';
import { useRescheduleFlow } from './hooks/use-reschedule-flow';

/** Discriminated union: when status is 'open', payload is present. TypeScript narrows on status. */
type RescheduleCabSheetState =
	| { status: 'closed' }
	| {
			status: 'open';
			fareData: RescheduleFareData;
			selectedCabId: string | undefined;
	  };

/** Discriminated union: when status is 'open', payload is present. TypeScript narrows on status. */
type PaymentBreakdownSheetState =
	| { status: 'closed' }
	| {
			status: 'open';
			selectedCab: RescheduleCabOption;
			fareData: RescheduleFareData;
	  };

export type RescheduleTripDetail = Pick<
	BookingSuccessResponse['data'],
	'rideDetails' | 'cabDetail' | 'rescheduleDetail' | 'parentBookingId'
>;

export interface RescheduleProps
	extends Omit<ResponsiveModalProps, 'children'> {
	open: boolean;
	onModalClosed: () => void;
	onRouterOperationStart: () => void;
	tripDetail: RescheduleTripDetail;
}

export function Reschedule({
	onModalClosed,
	onRouterOperationStart,
	tripDetail,
	open,
	...rest
}: RescheduleProps) {
	const { bookingId } = useParams({ from: '/_auth/trips/$bookingId/' });
	const [internalOpen, setInternalOpen] = useState(open);

	useEffect(() => {
		setInternalOpen(open);
	}, [open]);

	const initialPickupDateTime = tripDetail.rideDetails.pickup.dateTime.time;
	const { state, transitions, abort } = useRescheduleFlow({
		initialPickupDateTime,
	});

	const { step, dateTime, fareData, selectedCab, error } = state;
	const { setDateTime, goToStep } = transitions;
	const { cancel } = abort;

	const {
		handleContinue,
		handleCabConfirm,
		handleAmountAction,
		handleTryAgain,
		handleGetHelp,
		handleModalExited,
	} = useRescheduleActions({
		bookingId,
		initialPickupDateTime,
		dateTime,
		selectedCab,
		transitions,
		abort,
		onModalClosed,
		onRouterOperationStart,
	});

	const isFullscreen = step !== 'failure';

	const rescheduleCabSheet: RescheduleCabSheetState =
		step === 'selecting-cab' && fareData
			? { status: 'open', fareData, selectedCabId: selectedCab?.cabId }
			: { status: 'closed' };

	const PaymentBreakdownSheet: PaymentBreakdownSheetState =
		step === 'amount-due' && selectedCab && fareData
			? { status: 'open', selectedCab, fareData }
			: { status: 'closed' };

	const handleContinueWithDateUpdate = useCallback(
		(newDateTime?: string) => {
			if (newDateTime) setDateTime(newDateTime);
			return handleContinue(newDateTime);
		},
		[handleContinue, setDateTime],
	);

	const handleBack = useCallback(() => {
		setInternalOpen(false);
		cancel();
	}, [cancel]);

	const closeCabSheet = useCallback(() => goToStep('form'), [goToStep]);
	const closePaymentBreakdownSheet = useCallback(
		() => goToStep('selecting-cab'),
		[goToStep],
	);

	return (
		<Fragment>
			<ResponsiveModal
				open={internalOpen}
				onClose={handleBack}
				fullScreen={isFullscreen}
				transitionDirection={isFullscreen ? 'right' : 'up'}
				classes={{ container: 'overflow-y-auto flex flex-col' }}
				{...rest}
				TransitionProps={{
					onExited: () => {
						handleModalExited();
						onModalClosed();
					},
				}}
			>
				<RescheduleContent
					step={step}
					error={error}
					tripDetail={tripDetail}
					currentDateTime={dateTime}
					onContinue={handleContinueWithDateUpdate}
					onBack={handleBack}
					onGetHelp={handleGetHelp}
					onTryAgain={handleTryAgain}
				/>
			</ResponsiveModal>

			<BottomsheetExtended
				open={rescheduleCabSheet.status === 'open'}
				onClose={closeCabSheet}
				classes={{ container: 'h-auto pb-safe no-scrollbar' }}
			>
				{rescheduleCabSheet.status === 'open' && (
					<RescheduleCabSelection
						cabSelection={rescheduleCabSheet.fareData.tripDetails.cabSelection}
						note={rescheduleCabSheet.fareData.note}
						selectedCabId={rescheduleCabSheet.selectedCabId}
						parentBookingId={tripDetail.parentBookingId}
						onClose={closeCabSheet}
						onConfirm={handleCabConfirm}
					/>
				)}
			</BottomsheetExtended>

			<BottomsheetExtended
				open={PaymentBreakdownSheet.status === 'open'}
				onClose={closePaymentBreakdownSheet}
				showCloseButton
				classes={{ container: 'h-auto pb-safe no-scrollbar' }}
			>
				{PaymentBreakdownSheet.status === 'open' && (
					<PaymentBreakdown
						selectedCab={PaymentBreakdownSheet.selectedCab}
						fareData={PaymentBreakdownSheet.fareData}
						updatedDateTime={dateTime}
						onAction={handleAmountAction}
					/>
				)}
			</BottomsheetExtended>
		</Fragment>
	);
}
